import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import ConfirmPopup from "../ConfirmPopup/ConfirmPopup";

interface UnsavedChangesGuardContextValue {
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
  guardNavigation: (action: () => void) => void;
  registerSaveHandler: (handler: (() => Promise<boolean>) | null) => void;
}

const UnsavedChangesGuardContext =
  createContext<UnsavedChangesGuardContextValue | null>(null);

export const UnsavedChangesGuardProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [isSavingToContinue, setIsSavingToContinue] = useState(false);
  const [hasSaveHandler, setHasSaveHandler] = useState(false);
  const saveHandlerRef = React.useRef<(() => Promise<boolean>) | null>(null);

  const guardNavigation = useCallback(
    (action: () => void) => {
      if (!hasUnsavedChanges) {
        action();
        return;
      }
      setPendingAction(() => action);
    },
    [hasUnsavedChanges],
  );

  const registerSaveHandler = useCallback(
    (handler: (() => Promise<boolean>) | null) => {
      saveHandlerRef.current = handler;
      setHasSaveHandler(handler !== null);
    },
    [],
  );

  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const stayOnPage = () => setPendingAction(null);
  const leaveWithoutSaving = () => {
    const action = pendingAction;
    setPendingAction(null);
    action?.();
  };

  const saveAndContinue = async () => {
    const saveHandler = saveHandlerRef.current;
    if (!saveHandler || isSavingToContinue) return;

    setIsSavingToContinue(true);
    try {
      const saved = await saveHandler();
      if (!saved) return;

      const action = pendingAction;
      setHasUnsavedChanges(false);
      setPendingAction(null);
      action?.();
    } finally {
      setIsSavingToContinue(false);
    }
  };

  return (
    <UnsavedChangesGuardContext.Provider
      value={{
        hasUnsavedChanges,
        setHasUnsavedChanges,
        guardNavigation,
        registerSaveHandler,
      }}
    >
      {children}
      <ConfirmPopup
        blur
        isOpen={pendingAction !== null}
        title="Unsaved changes"
        message="You have unsaved changes. What would you like to do?"
        confirmLabel="Discard and leave"
        cancelLabel="Keep editing"
        saveLabel={isSavingToContinue ? "Saving..." : "Save and continue"}
        confirmColor="var(--color-brand)"
        confirmDisabled={isSavingToContinue}
        saveDisabled={!hasSaveHandler}
        onConfirm={leaveWithoutSaving}
        onCancel={stayOnPage}
        onSave={() => void saveAndContinue()}
      />
    </UnsavedChangesGuardContext.Provider>
  );
};

export const useUnsavedChangesGuard = (): UnsavedChangesGuardContextValue => {
  const context = useContext(UnsavedChangesGuardContext);
  if (!context) {
    throw new Error(
      "useUnsavedChangesGuard must be used within UnsavedChangesGuardProvider",
    );
  }
  return context;
};
